import type { ReactElement } from 'react';
import { useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';

import { type ImpersonationSession, useImpersonationSnapshot } from './impersonation-state';

type WriteModeConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewing: string;
  onConfirm: () => void;
};

export const useImpersonationWriteMode = (session: ImpersonationSession) => {
  const snapshot = useImpersonationSnapshot(session);
  const { identity, revision, customerUUID, writeModeEnabled } = snapshot;
  const blocked = customerUUID !== null && !writeModeEnabled;
  const [confirmingRevision, setConfirmingRevision] = useState<number | null>(null);
  const viewingName = identity?.name || 'this customer';
  const viewingRole = identity?.roleLabel ?? null;
  const viewing = viewingRole ? `${viewingName} (${viewingRole})` : viewingName;

  return {
    blocked,
    viewing,
    viewingName,
    viewingRole,
    requestWriteMode: () => setConfirmingRevision(revision),
    turnOffWriteMode: () => session.setWriteModeEnabled(false),
    confirmDialog: (
      <WriteModeConfirmDialog
        open={customerUUID !== null && confirmingRevision === revision}
        onOpenChange={(open) => setConfirmingRevision(open ? revision : null)}
        viewing={viewing}
        onConfirm={() => {
          if (confirmingRevision !== null)
            session.confirmWriteMode({ revision: confirmingRevision });
        }}
      />
    ),
  };
};

const WriteModeConfirmDialog = ({
  open,
  onOpenChange,
  viewing,
  onConfirm,
}: WriteModeConfirmDialogProps): ReactElement => (
  <Dialog.Root role="alertdialog" open={open} onOpenChange={onOpenChange}>
    <Dialog>
      <Col analyticsName="cake-impersonation-write-mode-confirm" trackImpression>
        <Dialog.Title className="pb-4 text-subheader-lg">Turn on write mode?</Dialog.Title>
        <Dialog.Description className="pb-6 text-body-md text-od-content-secondary">
          You are impersonating {viewing}. From now on, everything you change saves to their real
          account, including their offer.
        </Dialog.Description>
        <Row className="flex-col justify-end gap-3 md:flex-row">
          <Button
            variant="secondary"
            size="md"
            analyticsName="impersonation-write-mode-confirm-cancel"
            aria-label="Cancel"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="md"
            analyticsName="impersonation-write-mode-confirm-accept"
            aria-label="Turn it on"
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            Turn it on
          </Button>
        </Row>
      </Col>
    </Dialog>
  </Dialog.Root>
);
